import React from 'react';
import moment from 'moment-timezone';
import BravelyTimeZone from './timezone';
import airbrake from './airbrake';
import i18n from './i18n';

// ----------------------------------------------------------------------------
// Formatting Building Blocks
// ----------------------------------------------------------------------------
// "Fri"
const _DAY = 'ddd';
// "Friday"
const _DAY_FULL = 'dddd';
// "11"
const _DAY_NUMBER = 'D';
// "Nov 11"
const _DATE = 'MMM D';
// "Nov"
const _MONTH = 'MMM';
// "2019"
const _YEAR = 'YYYY';
// "3:19PM"
const _TIME = 'h:mmA';
// "EST"
const _ZONE_NAME = 'z';
// (GMT -05:00)
const _ZONE_GMT = '([GMT] Z)';

// ----------------------------------------------------------------------------
// Formatting Strings
// ----------------------------------------------------------------------------
// 7:00PM
const TWELVE_HOUR_TIME = _TIME;
// Nov 11, 2019
const SHORT_MONTH_DAY_YEAR = `${_DATE}, ${_YEAR}`;
// Nov 11, 2019 at 3:00PM
const SHORT_MONTH_DAY_YEAR_AND_TIME = `${_DATE}, ${_YEAR} ${TWELVE_HOUR_TIME}`;
// Mon, Nov 11
const SHORT_WEEKDAY_DATE = `${_DAY}, ${_DATE}`;
// Nov 11 at 3:00PM
const SHORT_DATE_AND_TIME = `${_DATE} [at] ${_TIME}`;
// Monday 3:00PM
const DAY_AND_TIME = `${_DAY_FULL} ${_TIME}`;
// Mon, Nov 11 at 3:00PM
const SHORT_WEEKDAY_DATE_AND_TIME = `${_DAY}, ${SHORT_DATE_AND_TIME}`;
// 3:00PM EST
const EVENT_DISPLAY_TIME_WITH_ZONE = `${_TIME} ${_ZONE_NAME}`;

// ----------------------------------------------------------------------------
// Date thresholds (in minutes)
// ----------------------------------------------------------------------------
export const THRESHOLD_CURRENT = -45;
export const THRESHOLD_SOON = 30;

export const formatDateByProximity = (
  dateStr,
  shouldFormatRelativeDate = true
) => {
  const FORMATTERS = {
    fullDate: formatMonthDayYearTimeWithZone,
    past: formatMonthDayYearTimeWithZone,
    today: formatTime12HourWithZone,
    thisWeek: formatDayTimeWithZone,
    upcoming: formatMonthDayTimeWithZone,
  };

  const scheduledDate = moment(dateStr);

  switch (true) {
    // Override date formatting logic and show full date
    case !shouldFormatRelativeDate:
      return FORMATTERS.fullDate(scheduledDate);
    // In the past
    case scheduledDate < moment().startOf('day'):
      return FORMATTERS.past(scheduledDate);
    // Today
    case scheduledDate < moment().endOf('day'):
      return FORMATTERS.today(scheduledDate);
    // This week
    case scheduledDate < moment().endOf('week'):
      return FORMATTERS.thisWeek(scheduledDate);
    // After this week
    case scheduledDate > moment().endOf('week'):
      return FORMATTERS.upcoming(scheduledDate);
    // Something went wrong, here's a date!
    default:
      return FORMATTERS.fullDate(scheduledDate);
  }
};

/**
 * @param {string} date
 * @returns {_ZONE_NAME|_ZONE_GMT}
 */
export function getZoneFormat(date, timeZone = BravelyTimeZone()) {
  // Empty Dates
  if (!!date === false) {
    return _ZONE_GMT;
  }
  try {
    // Extract zone abbr from date
    const zoneAbbreviation = moment.tz.zone(timeZone).abbr(moment(date));
    // Use name format if abbr is not an offset
    return zoneAbbreviation && !zoneAbbreviation.match(/\d+/g)
      ? _ZONE_NAME
      : _ZONE_GMT;
  } catch (exception) {
    // Send something helpful to Airbrake
    airbrake &&
      airbrake.notify({
        error: exception,
        context: { severity: 'warning' },
        params: {
          date: date,
          timeZone: timeZone,
        },
      });
    // Fallback to GMT if something goes wrong
    return _ZONE_GMT;
  }
}

/**
 *
 * @param {string} date
 * @param {string} timeZone
 * @returns {string} 5 hours ago
 */
export const formatDateTimeFromNow = (date, timeZone = BravelyTimeZone()) =>
  moment.tz(date, timeZone).fromNow();

/**
 * @param {string} date
 * @param {string} timeZone
 * @returns {string} Mon, Nov 11 at 3:00PM EST
 */
export const formatDateWeekdayDateTimeWithZone = (
  date,
  timeZone = BravelyTimeZone()
) =>
  date
    ? moment
        .tz(date, timeZone)
        .format(
          `${SHORT_WEEKDAY_DATE_AND_TIME} ${getZoneFormat(date, timeZone)}`
        )
    : '';

/**
 * @param {string} date
 * @param {string} timeZone
 * @returns {string} Mon, Nov 11
 */
export const formatDateWeekday = (date, timeZone = BravelyTimeZone()) => {
  if (!date) return '';

  const dayNumber = moment.tz(date, timeZone).format(_DAY_NUMBER);
  const month = moment.tz(date, timeZone).format(_MONTH);
  const day = moment.tz(date, timeZone).format(_DAY);

  const translatedDate = `${i18n.t(day)}, ${i18n.t(month)} ${i18n.t(
    dayNumber
  )}`;

  return translatedDate;
};

/**
 * @param {string} date
 * @param {string} timeZone
 * @returns {string} Nov 11, 2019
 */
export const formatDateMonthDayYear = (date, timeZone = BravelyTimeZone()) =>
  date ? moment.tz(date, timeZone).format(SHORT_MONTH_DAY_YEAR) : '';

/**
 * @param {string} date
 * @param {string} timeZone
 * @returns {string} 12:00PM
 */
export const formatTime12Hour = (date, timeZone = BravelyTimeZone()) =>
  date ? moment.tz(date, timeZone).format(TWELVE_HOUR_TIME) : '';

/**
 * @param {string} date
 * @param {string} timeZone
 * @returns {string} 12:00PM EST
 */
export const formatTime12HourWithZone = (date, timeZone = BravelyTimeZone()) =>
  date
    ? moment
        .tz(date, timeZone)
        .format(`${TWELVE_HOUR_TIME} ${getZoneFormat(date, timeZone)}`)
    : '';

/**
 * @param {string} date
 * @param {string} timeZone
 * @returns {string} Monday 3:00PM EST
 */
export const formatDayTimeWithZone = (date, timeZone = BravelyTimeZone()) =>
  date
    ? moment
        .tz(date, timeZone)
        .format(`${DAY_AND_TIME} ${getZoneFormat(date, timeZone)}`)
    : '';

/**
 * @param {string} date
 * @param {string} timeZone
 * @returns {string} Mon, Nov 11 at 12:00PM EST
 */
export const formatMonthDayTimeWithZone = (
  date,
  timeZone = BravelyTimeZone()
) =>
  date
    ? moment
        .tz(date, timeZone)
        .format(
          `${SHORT_WEEKDAY_DATE_AND_TIME} ${getZoneFormat(date, timeZone)}`
        )
    : '';

/**
 * @param {string} date
 * @param {string} timeZone
 * @returns {string} Nov 11, 2019 12:00PM EST
 */
export const formatMonthDayYearTimeWithZone = (
  date,
  timeZone = BravelyTimeZone()
) =>
  date
    ? moment
        .tz(date, timeZone)
        .format(
          `${SHORT_MONTH_DAY_YEAR_AND_TIME} ${getZoneFormat(date, timeZone)}`
        )
    : '';

/**
 * @param {string} date
 * @param {string} timeZone
 * @returns {string} an hour ago, 2 days ago, in one month, etc.
 */
export const formatRelativeDate = (date, timeZone = BravelyTimeZone()) =>
  date ? moment.tz(date, timeZone).fromNow() : '';

/**
 * @param {string} date
 * @param {string} timeZone
 * @returns {string} Wed, August 31 | 12:00PM EDT
 */
export const formatEventDate = (date, timeZone = BravelyTimeZone()) =>
  date
    ? moment
        .tz(date, timeZone)
        .format(`${SHORT_WEEKDAY_DATE} | ${EVENT_DISPLAY_TIME_WITH_ZONE}`)
    : '';

/**
 * @param {string} date
 * @param {object} styles
 * @returns {function}
 */
export function formatDateComponent(
  date,
  styles = {
    whiteSpace: 'nowrap',
  }
) {
  return (
    <time dateTime={date} title={formatDateWeekdayDateTimeWithZone(date)}>
      <span style={styles}>{formatDateWeekday(date)}</span>
      <span> {i18n.t('at')} </span>
      <span style={styles}>{formatTime12HourWithZone(date)}</span>
    </time>
  );
}
